/**
 * HHD "Select options" mobile ATC behavior
 *
 * On mobile, both Add to Cart buttons (sticky bar + in-page) submit the
 * currently preselected variant with no way to pick a size first. This makes
 * both buttons act as a "scroll to options" control — labeled "Choose
 * options" — until the shopper has actually picked a size, at which point
 * they behave like a normal Add to Cart button again.
 *
 * Scrolls to the top of the whole variant picker block (color swatches +
 * size), not just the size row, so a shopper who hasn't touched color either
 * sees both — not just size in isolation.
 *
 * Implemented as document-level event delegation (not a custom element) so
 * it keeps working after hhd-product-toggle.js replaces the product
 * section's innerHTML on MEN/WOMEN swap — delegated listeners don't care
 * that the underlying button/fieldset nodes were just recreated.
 */
(function () {
  var MOBILE_QUERY = "(max-width: 767px)";
  var SIZE_SELECTOR = ".hhd-variant--size";
  var VARIANT_BLOCK_SELECTOR = ".product__block--variant_picker_hhd";
  var BTN_SELECTOR = ".hhd-sticky-atc__atc-btn, .product-form__submit-hhd";

  var currentSizeFieldset = null;
  var sizeChosen = false;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  // Re-derives state whenever the size fieldset in the DOM changes identity
  // (e.g. after a MEN/WOMEN toggle recreates the whole product info block),
  // so "has the shopper chosen a size" naturally resets per product.
  function syncProductState() {
    var fieldset = document.querySelector(SIZE_SELECTOR);
    if (fieldset !== currentSizeFieldset) {
      currentSizeFieldset = fieldset;
      sizeChosen = false;
    }
    return fieldset;
  }

  function isBtnBusy(btn) {
    return (
      btn.hasAttribute("disabled") ||
      btn.getAttribute("aria-disabled") === "true" ||
      btn.classList.contains("btn--loading")
    );
  }

  function addToCartLabel() {
    return (
      (window.FoxTheme &&
        FoxTheme.variantStrings &&
        FoxTheme.variantStrings.addToCart) ||
      "Add to cart"
    );
  }

  function chooseOptionsLabel() {
    return (
      (window.FoxTheme &&
        FoxTheme.variantStrings &&
        FoxTheme.variantStrings.chooseOptions) ||
      "Select options"
    );
  }

  // Other scripts (variant-selects.js, hhd-sticky-atc.js) write "Add to
  // cart"/"Sold out" into these buttons whenever the variant changes —
  // synchronously and, for the in-page button, again later after an async
  // fetch resolves. Re-asserting the desired label here on every relevant
  // event/mutation is what keeps "Select options" from being stomped.
  function desiredLabel(btn) {
    if (isBtnBusy(btn)) return null;

    var fieldset = syncProductState();
    if (fieldset && isMobile() && !sizeChosen) {
      return chooseOptionsLabel();
    }
    return addToCartLabel();
  }

  function enforceLabels() {
    document.querySelectorAll(BTN_SELECTOR).forEach(function (btn) {
      var span = btn.querySelector("span");
      if (!span) return;

      var label = desiredLabel(btn);
      if (label !== null && span.textContent.trim() !== label) {
        span.textContent = label;
      }
    });
  }

  function headerOffset() {
    var header =
      document.querySelector(".header-section") ||
      document.querySelector("header");
    return header ? header.offsetHeight : 0;
  }

  function stickyBarOffset() {
    var stickyBar = document.querySelector(".hhd-sticky-atc--visible");
    return stickyBar ? stickyBar.offsetHeight : 0;
  }

  // Land on the top of the whole variant picker block (color swatches +
  // size), not just the size fieldset, so color is visible too. Falls back
  // to <variant-selects> and finally the size fieldset itself if the block
  // wrapper isn't found, so this never breaks even if markup changes.
  function scrollTarget(fieldset) {
    return (
      fieldset.closest(VARIANT_BLOCK_SELECTOR) ||
      fieldset.closest("variant-selects") ||
      fieldset
    );
  }

  function scrollToOptions(fieldset) {
    var target = scrollTarget(fieldset);
    var offset = headerOffset() + stickyBarOffset() + 16;
    var top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  }

  // Capture phase: runs before the sticky button's own click listener
  // (hhd-sticky-atc.js) and before the in-page button's native form submit,
  // so stopPropagation()/preventDefault() here reliably blocks both without
  // touching their existing logic.
  document.addEventListener(
    "click",
    function (e) {
      var btn = e.target.closest(BTN_SELECTOR);
      if (!btn || isBtnBusy(btn) || !isMobile()) return;

      var fieldset = syncProductState();
      if (!fieldset || sizeChosen) return;

      e.preventDefault();
      e.stopPropagation();
      scrollToOptions(fieldset);
    },
    true,
  );

  document.addEventListener("change", function (e) {
    var fieldset = syncProductState();
    if (!fieldset) return;

    if (e.target.closest(SIZE_SELECTOR)) {
      sizeChosen = true;
    }

    enforceLabels();
  });

  document.addEventListener("variant:changed", enforceLabels);
  window.addEventListener("resize", enforceLabels);

  // Catches the async label overwrite from variant-selects.js's
  // renderProductInfo() fetch, and survives hhd-product-toggle.js's
  // section-wide innerHTML swap without needing to re-query/re-init.
  var observer = new MutationObserver(enforceLabels);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
  });

  enforceLabels();
})();
